import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Sparkles, Bot, User, Download, Cpu, AlertTriangle, Loader2, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme, ThemeProvider } from '../contexts/ThemeContext';
import * as webllm from '@mlc-ai/web-llm';

interface Message {
    role: 'user' | 'assistant';
    content: string;
}

type AppState = 'checking' | 'unsupported' | 'ready' | 'downloading' | 'loaded' | 'error';

// Same system prompt as the Groq API, adapted for Qwen3
const SYSTEM_PROMPT = `You are Vansh Mittal's friendly AI assistant on his portfolio website. Help visitors learn about Vansh's background, skills, projects, and experiences. Be conversational, helpful, and engaging.

=== ABOUT VANSH MITTAL ===
Name: Vansh Mittal
Location: Florida, USA
Education: B.S. Computer Engineering at University of Florida (Expected May 2027)
- Dean's Honor List recipient (4 semesters)
- Focusing on embedded systems, AI/ML, and full-stack development
Contact: mittal.vansh@ufl.edu | (407) 782-7702

=== CURRENT ROLES ===
1. Freelance Developer
   - Building custom web solutions for clients
   - E-commerce development and consulting

2. Researcher at Machine Intelligence Laboratory (August 2025 - Present)
   - Collaborating on robotics and AI research projects
   - Working with embedded systems and intelligent agents

3. Event Operations at Indian Student Association @ UF (Spring 2024 - Present)
   - Coordinating logistics for large-scale cultural festivals

=== FEATURED PROJECTS ===

1. BUZSHOES.CA E-COMMERCE
   - Built a responsive e-commerce storefront for a Canadian sneaker brand
   - Integrated Shopify APIs for secure checkout and inventory sync
   - Technologies: Framer, Shopify API, E-commerce, UX Research

2. GATORGATHER
   - Event platform for UF students with real-time mapping
   - React, Firebase Auth, Google Maps API

3. LLM OPS TOOLKIT
   - Local experimentation workflow for Gemma and Qwen language models
   - PyTorch, CUDA, LangGraph

=== TECHNICAL SKILLS ===
Languages: C++, C, Python, Assembly (speaks English and Hindi)
Stack: PyTorch, CUDA, React, Firebase Auth, Shopify API, Framer, TypeScript, Tailwind CSS
Tools: GitHub, MATLAB, VHDL, FPGA, Autodesk Inventor, Intel Quartus, WSL, Linux CLI

=== INTERESTS ===
- LLM Fine-tuning and optimization
- Agentic AI systems (LangGraph, autonomous agents)
- Embedded systems and robotics
- Full-stack web development

=== RESPONSE GUIDELINES ===
- Be friendly and conversational
- Answer questions about Vansh's background, projects, skills, education
- Keep responses concise (2-4 sentences for simple questions)
- For unrelated questions, gently redirect to asking about Vansh
`;

// Function to extract response after </think> tag or strip think blocks
function extractResponse(text: string): string {
    // Check if there's a closing </think> tag - extract everything after it
    const thinkEndMatch = text.match(/<\/think>\s*([\s\S]*)/i);
    if (thinkEndMatch && thinkEndMatch[1]) {
        return thinkEndMatch[1].trim();
    }

    // If we're still in the middle of thinking (no </think> yet), return empty
    // so we don't show partial thinking
    if (/<think>/i.test(text) && !/<\/think>/i.test(text)) {
        return '';
    }

    // No think tags at all - return as-is
    return text.trim();
}

// Top Navigation Bar component
function TopNavbar() {
    const { theme, toggleTheme } = useTheme();
    const isDark = theme === 'dark';

    return (
        <nav className="fixed top-0 left-0 right-0 z-50">
            <div
                className={`backdrop-blur-xl shadow-lg transition-all duration-300 ${isDark
                    ? 'bg-white/10 border-b border-white/10'
                    : 'bg-black/5 border-b border-black/10'
                    }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center justify-between h-16 sm:h-20">
                        {/* Logo and Name - Links to home */}
                        <a
                            href="/"
                            className="flex items-center gap-3 group"
                        >
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[var(--color-uf-blue)] to-[var(--color-uf-orange)] flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-105">
                                <span className="text-white text-sm sm:text-base font-semibold tracking-[0.2em]">VM</span>
                            </div>
                            <span className={`hidden sm:block font-medium tracking-wide ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Vansh Mittal
                            </span>
                        </a>

                        {/* Center Title */}
                        <div className="absolute left-1/2 -translate-x-1/2">
                            <span className={`text-sm sm:text-base font-medium tracking-wide ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                                WebGPU
                            </span>
                        </div>

                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className={`p-2.5 rounded-xl transition-all duration-300 ${isDark
                                ? 'hover:bg-white/10'
                                : 'hover:bg-black/10'
                                }`}
                            aria-label="Toggle theme"
                        >
                            <motion.div
                                key={theme}
                                initial={{ rotate: -90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: 90, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                {isDark ? (
                                    <Sun className={`w-5 h-5 ${isDark ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`} />
                                ) : (
                                    <Moon className={`w-5 h-5 ${isDark ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`} />
                                )}
                            </motion.div>
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
}

function WebGPUChatContent() {
    const [appState, setAppState] = useState<AppState>('checking');
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [statusText, setStatusText] = useState('Checking WebGPU support...');
    const [errorMessage, setErrorMessage] = useState('');
    const [messages, setMessages] = useState<Message[]>([
        { role: 'assistant', content: "Hi! I'm Vansh's AI assistant running locally on your device via WebGPU. Ask me anything about his projects, skills, or experience!" }
    ]);
    const [input, setInput] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [streamingContent, setStreamingContent] = useState('');

    const engineRef = useRef<webllm.MLCEngine | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // Check WebGPU support on mount
    useEffect(() => {
        const checkWebGPU = async () => {
            try {
                if (!navigator.gpu) {
                    setAppState('unsupported');
                    setErrorMessage('WebGPU is not supported in your browser. Please use Chrome 113+, Edge 113+, or Chrome on Android 121+.');
                    return;
                }

                const adapter = await navigator.gpu.requestAdapter();
                if (!adapter) {
                    setAppState('unsupported');
                    setErrorMessage('No WebGPU adapter found. Your device may not support WebGPU.');
                    return;
                }

                setAppState('ready');
                setStatusText('WebGPU is supported! Click below to download the AI model.');
            } catch (error) {
                setAppState('unsupported');
                setErrorMessage('Failed to initialize WebGPU: ' + (error as Error).message);
            }
        };

        checkWebGPU();
    }, []);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, streamingContent, scrollToBottom]);

    useEffect(() => {
        if (appState === 'loaded') {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [appState]);

    const initializeModel = async () => {
        setAppState('downloading');
        setDownloadProgress(0);
        setStatusText('Initializing model download...');

        try {
            const engine = await webllm.CreateMLCEngine('Qwen3-0.6B-q4f16_1-MLC', {
                initProgressCallback: (progress) => {
                    setDownloadProgress(progress.progress);
                    setStatusText(progress.text);
                }
            });

            engineRef.current = engine;
            setAppState('loaded');
            setStatusText('Model loaded! Start chatting.');
        } catch (error) {
            console.error('Model initialization error:', error);
            setAppState('error');
            setErrorMessage('Failed to load the model: ' + (error as Error).message);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isGenerating || !engineRef.current) return;

        const userMessage = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsGenerating(true);
        setStreamingContent('');

        try {
            const chatHistory = messages.map(m => ({
                role: m.role as 'user' | 'assistant',
                content: m.content
            }));

            const chunks = await engineRef.current.chat.completions.create({
                messages: [
                    { role: 'system', content: SYSTEM_PROMPT },
                    ...chatHistory,
                    { role: 'user', content: userMessage }
                ],
                temperature: 0.6, // Balanced for conversational responses
                max_tokens: 400, // Allow fuller responses
                stream: true,
            });

            let fullResponse = '';
            for await (const chunk of chunks) {
                const delta = chunk.choices[0]?.delta?.content || '';
                fullResponse += delta;
                // Extract response after </think> tag for display
                const displayContent = extractResponse(fullResponse);
                setStreamingContent(displayContent);
            }

            // Extract final response after </think> tag
            const cleanedResponse = extractResponse(fullResponse);
            setMessages(prev => [...prev, { role: 'assistant', content: cleanedResponse || fullResponse }]);
            setStreamingContent('');
        } catch (error) {
            console.error('Chat error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "Sorry, I encountered an error generating a response. Please try again."
            }]);
        } finally {
            setIsGenerating(false);
        }
    };

    // Theme-aware colors (same as AIChat)
    const colors = {
        containerBg: isDark
            ? 'rgba(18, 18, 25, 0.95)'
            : 'rgba(247, 245, 239, 0.98)',
        sectionBg: isDark
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
        border: isDark
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.06)',
        text: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 30, 30, 0.9)',
        textMuted: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 30, 30, 0.5)',
        inputBg: isDark
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.03)',
        assistantBubble: isDark
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.04)',
    };

    // Render loading/setup states
    if (appState !== 'loaded') {
        return (
            <>
                <TopNavbar />
                <div
                    className="min-h-screen flex items-center justify-center p-4 pt-24 sm:pt-28"
                    style={{ backgroundColor: isDark ? '#0a0a0f' : '#f7f5ef' }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-md w-full p-8 rounded-3xl backdrop-blur-xl"
                        style={{
                            backgroundColor: colors.containerBg,
                            border: `1px solid ${colors.border}`,
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center gap-4 mb-6">
                            <div
                                className="p-3 rounded-2xl"
                                style={{
                                    backgroundColor: 'rgba(0, 82, 147, 0.15)',
                                    border: '1px solid rgba(0, 82, 147, 0.25)',
                                }}
                            >
                                <Cpu className="w-8 h-8 text-[var(--color-uf-blue)]" />
                            </div>
                            <div>
                                <h1 className="text-xl font-semibold" style={{ color: colors.text }}>
                                    WebGPU Local AI
                                </h1>
                                <p className="text-sm" style={{ color: colors.textMuted }}>
                                    Qwen3-0.6B running in your browser
                                </p>
                            </div>
                        </div>

                        {/* State-specific content */}
                        {appState === 'checking' && (
                            <div className="text-center py-8">
                                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: colors.textMuted }} />
                                <p style={{ color: colors.text }}>{statusText}</p>
                            </div>
                        )}

                        {appState === 'unsupported' && (
                            <div className="text-center py-4">
                                <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                                <p className="mb-4" style={{ color: colors.text }}>{errorMessage}</p>
                                <p className="text-sm" style={{ color: colors.textMuted }}>
                                    Supported browsers: Chrome 113+, Edge 113+, Chrome Android 121+
                                </p>
                            </div>
                        )}

                        {appState === 'ready' && (
                            <div className="text-center py-4">
                                <p className="mb-6" style={{ color: colors.text }}>{statusText}</p>
                                <div
                                    className="p-4 rounded-xl mb-6"
                                    style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}` }}
                                >
                                    <p className="text-sm" style={{ color: colors.textMuted }}>
                                        <strong>Note:</strong> The model (~500MB) will be downloaded and cached locally.
                                        Subsequent visits will load instantly from cache.
                                    </p>
                                </div>
                                <button
                                    onClick={initializeModel}
                                    className="w-full py-3 px-6 rounded-xl bg-[var(--color-uf-blue)] text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                                >
                                    <Download className="w-5 h-5" />
                                    Download & Start
                                </button>
                            </div>
                        )}

                        {appState === 'downloading' && (
                            <div className="py-4">
                                <div className="mb-4">
                                    <div
                                        className="h-2 rounded-full overflow-hidden"
                                        style={{ backgroundColor: colors.inputBg }}
                                    >
                                        <motion.div
                                            className="h-full bg-[var(--color-uf-blue)]"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${downloadProgress * 100}%` }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    </div>
                                    <p className="text-sm mt-2 text-center" style={{ color: colors.textMuted }}>
                                        {Math.round(downloadProgress * 100)}%
                                    </p>
                                </div>
                                <p className="text-sm text-center" style={{ color: colors.text }}>
                                    {statusText}
                                </p>
                            </div>
                        )}

                        {appState === 'error' && (
                            <div className="text-center py-4">
                                <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                                <p className="mb-4" style={{ color: colors.text }}>{errorMessage}</p>
                                <button
                                    onClick={() => setAppState('ready')}
                                    className="py-2 px-4 rounded-xl bg-[var(--color-uf-blue)] text-white font-medium hover:opacity-90 transition-opacity"
                                >
                                    Try Again
                                </button>
                            </div>
                        )}
                    </motion.div>
                </div>
            </>
        );
    }

    // Main chat interface (same style as AIChat)
    return (
        <>
            <TopNavbar />
            <div
                className="min-h-screen flex items-center justify-center p-4 pt-24 sm:pt-28"
                style={{ backgroundColor: isDark ? '#0a0a0f' : '#f7f5ef' }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-2xl h-[80vh] max-h-[700px] flex flex-col rounded-3xl backdrop-blur-xl overflow-hidden"
                    style={{
                        backgroundColor: colors.containerBg,
                        border: `1px solid ${colors.border}`,
                    }}
                >
                    {/* Header */}
                    <div
                        className="p-4 flex items-center gap-3"
                        style={{
                            backgroundColor: colors.sectionBg,
                            borderBottom: `1px solid ${colors.border}`,
                        }}
                    >
                        <div
                            className="p-2 rounded-full"
                            style={{
                                backgroundColor: 'rgba(0, 82, 147, 0.15)',
                                border: '1px solid rgba(0, 82, 147, 0.25)',
                            }}
                        >
                            <Bot className="w-5 h-5 text-[var(--color-uf-blue)]" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-medium text-sm" style={{ color: colors.text }}>
                                Vansh AI (Local)
                            </h3>
                            <p className="text-xs flex items-center gap-1" style={{ color: colors.textMuted }}>
                                <Cpu className="w-3 h-3" />
                                Running on your device via WebGPU
                            </p>
                        </div>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                            >
                                <div
                                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                                    style={{
                                        backgroundColor: msg.role === 'assistant'
                                            ? 'rgba(0, 82, 147, 0.15)'
                                            : 'rgba(250, 70, 22, 0.15)',
                                        border: `1px solid ${msg.role === 'assistant'
                                            ? 'rgba(0, 82, 147, 0.25)'
                                            : 'rgba(250, 70, 22, 0.25)'}`,
                                    }}
                                >
                                    {msg.role === 'assistant'
                                        ? <Sparkles className="w-4 h-4 text-[var(--color-uf-blue)]" />
                                        : <User className="w-4 h-4 text-[var(--color-uf-orange)]" />}
                                </div>
                                <div
                                    className={`max-w-[75%] p-3 rounded-2xl text-sm leading-relaxed
                  ${msg.role === 'assistant' ? 'rounded-tl-none' : 'rounded-tr-none'}`}
                                    style={{
                                        backgroundColor: msg.role === 'assistant'
                                            ? colors.assistantBubble
                                            : 'var(--color-uf-blue)',
                                        color: msg.role === 'assistant' ? colors.text : 'white',
                                        border: msg.role === 'assistant' ? `1px solid ${colors.border}` : 'none',
                                        boxShadow: msg.role === 'user' ? '0 4px 12px rgba(0, 82, 147, 0.2)' : 'none',
                                    }}
                                >
                                    {msg.content}
                                </div>
                            </div>
                        ))}

                        {/* Streaming response */}
                        {streamingContent && (
                            <div className="flex gap-3">
                                <div
                                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                                    style={{
                                        backgroundColor: 'rgba(0, 82, 147, 0.15)',
                                        border: '1px solid rgba(0, 82, 147, 0.25)',
                                    }}
                                >
                                    <Sparkles className="w-4 h-4 text-[var(--color-uf-blue)]" />
                                </div>
                                <div
                                    className="max-w-[75%] p-3 rounded-2xl rounded-tl-none text-sm leading-relaxed"
                                    style={{
                                        backgroundColor: colors.assistantBubble,
                                        color: colors.text,
                                        border: `1px solid ${colors.border}`,
                                    }}
                                >
                                    {streamingContent}
                                    <span className="inline-block w-1.5 h-4 ml-0.5 bg-[var(--color-uf-blue)] animate-pulse" />
                                </div>
                            </div>
                        )}

                        {/* Loading indicator */}
                        {isGenerating && !streamingContent && (
                            <div className="flex gap-3">
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center"
                                    style={{
                                        backgroundColor: 'rgba(0, 82, 147, 0.15)',
                                        border: '1px solid rgba(0, 82, 147, 0.25)',
                                    }}
                                >
                                    <Bot className="w-4 h-4 text-[var(--color-uf-blue)]" />
                                </div>
                                <div
                                    className="p-3 rounded-2xl rounded-tl-none flex items-center gap-1"
                                    style={{
                                        backgroundColor: colors.assistantBubble,
                                        border: `1px solid ${colors.border}`,
                                    }}
                                >
                                    <span
                                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                                        style={{ backgroundColor: colors.textMuted, animationDelay: '0ms' }}
                                    />
                                    <span
                                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                                        style={{ backgroundColor: colors.textMuted, animationDelay: '150ms' }}
                                    />
                                    <span
                                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                                        style={{ backgroundColor: colors.textMuted, animationDelay: '300ms' }}
                                    />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div
                        className="p-4"
                        style={{
                            backgroundColor: colors.sectionBg,
                            borderTop: `1px solid ${colors.border}`,
                        }}
                    >
                        <form onSubmit={handleSubmit} className="relative">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask about Vansh's resume..."
                                disabled={isGenerating}
                                className="w-full rounded-xl pl-4 pr-12 py-3 text-sm transition-all focus:outline-none disabled:opacity-50"
                                style={{
                                    backgroundColor: colors.inputBg,
                                    border: `1px solid ${colors.border}`,
                                    color: colors.text,
                                }}
                            />
                            <button
                                type="submit"
                                disabled={!input.trim() || isGenerating}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-[var(--color-uf-blue)] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--color-uf-blue)]/80 transition-colors"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </div>
                </motion.div>
            </div>
        </>
    );
}

// Wrap with ThemeProvider for standalone page
export default function WebGPUChat() {
    return (
        <ThemeProvider>
            <WebGPUChatContent />
        </ThemeProvider>
    );
}
